/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from "react";
import { useCurrencyFormatter, useDateFormatter } from "hooks";
import { useDispatch, useSelector } from "react-redux";
import { getBiller } from "selectors";
import {
  BannerProps,
  Tooltip,
  TooltipEnums,
} from "kubra-ux-forge";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { Navigate, useNavigate } from "react-router-dom";
import routing from "routing";
import { useTranslation } from "react-i18next";
import { LOOKUP_NAMESPACE } from "constants/i18n-namespaces";
import { getDomain, getLabelWithKrn } from "utilities/helper";
import { getLabelsWithKrn } from "selectors";
import {
  getPayableFromSession,
  postPayableInitiateIntent,
  postPayableUpdate,
} from "api/IamApi/entities/ezpay";
import { IPayableSessionData } from "types/payableSession";
import { setPayableSession } from "actions/payableSessionActions";
import ContinueButton from "components/ezpay/payables/continue-button/continue-button";
import { IContactInfo } from "types/biller";
import { InvoicePayable } from "types/payable";
import { getPayableSession } from "selectors/payableSession";
import { getDeviceFingerprint } from "selectors/appState";
import { PayableBreadcrumb } from "components/ezpay/common/payable-breadcrumb";

export interface IInvoiceListProps {
  setBannerState: (state: BannerProps) => void;
}

export const InvoiceList = (props: IInvoiceListProps) => {
  const { t } = useTranslation([LOOKUP_NAMESPACE]);
  const fingerprint = useSelector(getDeviceFingerprint);

  const { setBannerState } = props;
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [selectedInvoice, setSelectedInvoice] = useState<InvoicePayable | null>(
    null
  );
  const currencyFormatter = useCurrencyFormatter();
  const dateFormatter = useDateFormatter();

  const labelSourceWithKrn = useSelector(getLabelsWithKrn);

  const payableSession = useSelector(getPayableSession);

  // Assuming if we get to this page, that all payables will be of type invoice.
  const invoiceList = payableSession?.lookupResults as InvoicePayable[];
  const selectedPayableType = payableSession.payableType;

  const dispatch = useDispatch();
  const biller = useSelector(getBiller);

  const handleInvoiceClick = (newPayable: InvoicePayable) => {
    setSelectedInvoice(newPayable);
  };

  const contactUs = biller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  }) as IContactInfo;

  const handleContinueClick = async () => {
    if (!selectedInvoice) {
      setBannerState({
        active: true,
        variant: "error",
        messageLabel: t("no-invoice-selected-error"),
        messageBody: undefined,
        withIcon: true,
        withHyperlink: false,
      });
      return;
    }

    if (!selectedPayableType) {
      console.error("Payable type not available");
      return;
    }

    setLoading(true);

    if (selectedPayableType.extensionFields?.length > 0) {
      const payableUpdateResponse = await postPayableUpdate(
        selectedInvoice.krn
      );

      dispatch(setPayableSession(payableUpdateResponse.data));
      navigate(routing.payableDetails.index());
      setLoading(false);
    } else {
      try {
        await postPayableInitiateIntent(
          selectedInvoice.krn,
          fingerprint,
          navigator.userAgent
        );

        const domain = getDomain();

        // TODO: review and see if it makes sense to have the /payable/initiate endpoint return the payable session object.
        const updatedPayableSession: IPayableSessionData =
          await getPayableFromSession(domain);

        dispatch(setPayableSession(updatedPayableSession));
        navigate(routing.payableDashboard.index());
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        let messageLabel = t("generic-lookup-error");
        let messageBody = undefined;

        // TODO: remove this check here as selectedPayableType cannot be "account". So this check is unnecessary here.
        if (
          error.errorMessageCode === 404 &&
          selectedPayableType?.category.toLowerCase() === "account"
        ) {
          messageLabel = t("invalid-entry");
          messageBody = t("validation-error", {
            billedName: biller?.billerName,
            contactNo: contactUs.properties.phone,
          });
        } else if (error.errorMessageCode === 404) {
          messageLabel = t("payable-lookup-error", {
            payableName: getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, selectedPayableType.typeKrn, selectedPayableType.name),
          });
          messageBody = undefined;
        }

        setBannerState({
          active: true,
          variant: "error",
          messageLabel,
          messageBody,
          withClose: true,
          withIcon: true,
          withHyperlink: false,
        });
      }
    }
  };

  if (!invoiceList || !selectedPayableType) {
    console.error("Payable list and/or payable type not available");
    // TODO: show error or navigate to index route?
    return <Navigate to={routing.index()} />;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <PayableBreadcrumb />
      </div>

      <div className="ez-pay-payable-row">
        {t("invoice-list-info", {
          lookupName: getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, selectedPayableType.typeKrn, selectedPayableType.name),
        })}
      </div>

      <div className="ez-pay-payable-row">
        {invoiceList?.map((invoice) => {
          const isSelected = selectedInvoice?.krn === invoice.krn;
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              className="invoice-list-item-container"
              key={invoice.krn}
              onClick={() => handleInvoiceClick(invoice)}
              style={{
                backgroundColor: isSelected ? "#E3F3FF" : "transparent",
                border: isSelected ? "2px solid #279ACE" : "1px solid #B5B5B5",
              }}
            >
              <div className="invoice-item-heading-container">
                <span className="invoice-list-item-heading">
                  {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, invoice.typeKrn, "invoiceNumber")}
                </span>
                |{" "}
                <span className="invoice-list-item-value">
                  {invoice.invoiceNumber}
                </span>
              </div>
              <div className="invoice-list-item-divider" />
              <div className="invoice-list-item-sub-heading-container">
                {invoice.dueDate && (
                  <div className="invoice-list-item-sub-heading">
                    <div className="invoice-list-item-heading">
                      {t("due-date-label")}
                    </div>
                    <div>{dateFormatter.shortIsoFormat(invoice.dueDate)}</div>
                  </div>
                )}
                <div className="invoice-list-item-sub-heading">
                  <div className="invoice-list-item-heading">
                    {t("current-amount-due-label")}{" "}
                    <Tooltip
                      direction={TooltipEnums.TopRight}
                      content={`${t("current-amount-due-tooltip")}`}
                      data-cy="current-amount-due-tooltip"
                      variant="rich"
                    />
                  </div>
                  <div>
                    {invoice.payableAmount == null
                      ? "-"
                      : currencyFormatter.format(invoice.payableAmount)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <ContinueButton loading={loading} onClick={handleContinueClick} />
      <ContactAndTocFooter biller={biller} />
    </div>
  );
};
